import { useSyncExternalStore } from "react";
import { flushSync } from "react-dom";

export type ProductionType = "lyric_video" | "motion_music_video";
export interface IntakeSourceValues {
  title: string;
  artist: string;
  lyrics: string;
  video_description: string;
  avatar_id: string;
  production_type: ProductionType;
  noLyrics: boolean;
  startWithAi: boolean;
  scene_count: number;
  /** Iris's question for a sad or serious song: how the singer looks ("" until answered). */
  singer_expression: string;
}
export interface IntakeQuoteView {
  hidden: boolean;
  total: string;
  scope: string;
  motion: string;
  limit: string;
  message: string;
  disabled: boolean;
  label: string;
}
export interface IntakeSourceState {
  values: IntakeSourceValues;
  avatars: { id: string; name: string }[];
  artistHint: string;
  avatarHint: string;
  shotLabel: string;
  shotRecommendation: string;
  quote: IntakeQuoteView;
  error: string;
  submitting: boolean;
  writing: boolean;
  writerLabel: string;
  returnToIntake: boolean;
  /** While Iris talks the screen is only the conversation; the story panel shows in step-by-step. */
  chatOnly: boolean;
  /** The maker looks at the story panel for a moment while still chatting. */
  peek: boolean;
}
export type SourceAction = "audio" | "motion_reference" | "environment_reference" | "noLyrics" | "startWithAi" | "production_type" | "scene_count" | "avatar_id" | "changed" | "closed" | "write" | "resume";
export interface IntakeSourceHandlers {
  action: (action: SourceAction, value?: string) => void;
  /** prepareSong: Iris's handoff after the treatment also prepares the song once the production exists (#99). */
  submit: (form: HTMLFormElement, options?: { prepareSong?: boolean }) => Promise<void>;
}
export const defaultIntakeValues = (): IntakeSourceValues => ({ title: "", artist: "", lyrics: "", video_description: "", avatar_id: "", production_type: "lyric_video", noLyrics: false, startWithAi: true, scene_count: 18, singer_expression: "" });
export const initialQuote = (): IntakeQuoteView => ({ hidden: false, total: "Checking credits...", scope: "Checking...", motion: "Checking...", limit: "Checking server limit...", message: "Nothing starts until this current quote is confirmed.", disabled: true, label: "Checking credits..." });
let state: IntakeSourceState = {
  values: defaultIntakeValues(), avatars: [], artistHint: "Enter the artist once; Caprica will remember it for your next production.",
  avatarHint: "An approved avatar gives the film a recognizable, consistent lead character.",
  shotLabel: "Calculated after audio", shotRecommendation: "Caprica uses the song duration to recommend a native 6-second pace. You can still adjust it here.",
  quote: initialQuote(), error: "", submitting: false, writing: false, writerLabel: "Fill my production form", returnToIntake: false, chatOnly: false, peek: false,
};
const listeners = new Set<() => void>();
let handlers: IntakeSourceHandlers | undefined;
export function registerIntakeSourceHandlers(next: IntakeSourceHandlers) { handlers = next; return () => { if (handlers === next) handlers = undefined; }; }
export function getIntakeSource() { return state; }
export function updateIntakeSource(patch: Partial<IntakeSourceState>) {
  flushSync(() => { state = { ...state, ...patch }; for (const listener of listeners) listener(); });
}
export function setIntakeValues(patch: Partial<IntakeSourceValues>) {
  const values = { ...state.values, ...patch };
  if (values.noLyrics) values.production_type = "motion_music_video";
  updateIntakeSource({ values });
}
export function resetIntakeSource() {
  updateIntakeSource({ values: defaultIntakeValues(), error: "", submitting: false, writing: false, quote: initialQuote(), shotLabel: "Calculated after audio", writerLabel: "Fill my production form", peek: false });
}
export function useIntakeSource() {
  return useSyncExternalStore((listener) => { listeners.add(listener); return () => listeners.delete(listener); }, getIntakeSource, getIntakeSource);
}
export function intakeSourceAction(action: SourceAction, value?: string) { handlers?.action(action, value); }
export async function submitIntakeSource(form: HTMLFormElement, options?: { prepareSong?: boolean }) {
  if (!handlers || state.submitting || state.writing || state.quote.disabled) return;
  const invalid = form.querySelector<HTMLInputElement>(":invalid");
  if (invalid) {
    const label = invalid.labels?.[0]?.textContent?.replace(/\u00b7.*$/, "").trim() || "required field";
    updateIntakeSource({ error: `Please complete ${label} before creating the project.` });
    const details = invalid.closest("details"); if (details) details.open = true;
    invalid.focus(); return;
  }
  // Acquire synchronously: requestSubmit and the draft approval button share this gate.
  updateIntakeSource({ submitting: true, error: "" });
  try { await (options ? handlers.submit(form, options) : handlers.submit(form)); }
  catch (error) { updateIntakeSource({ error: error instanceof Error ? error.message : String(error) }); }
  finally { updateIntakeSource({ submitting: false }); intakeSourceAction("changed"); }
}
